import { memo, useCallback, useState } from 'react'
import { CSSObjectWithLabel } from 'react-select'
import { ascending, entity, like, query, string, some } from '~/prix'
import unaccent from '~/prix/functions/unaccent'
import SelectFromQuery from '~/prix/react/components/form/inputs/selectFromQuery'

interface SelectSegmentFilterProps {
  onSelectSegment: (segment: { id: string; description: string } | null) => void
  placeholder?: string
  value?: { id: string; description: string } | null
}

const SelectSegmentFilter = ({
  onSelectSegment,
  placeholder = 'Selecione um segmento',
  value,
}: SelectSegmentFilterProps) => {
  const [isDisabled, setIsDisabled] = useState(false)

  const segmentListQueryFactory = useCallback(
    (input: string) =>
      query('segment')
        .select({
          id: entity('segment').property('id'),
          description: entity('segment').property('description'),
        })
        .where(
          some(
            like(
              unaccent(entity('segment').property('description')),
              unaccent(string().value(`%${input.replace(/\s/g, '%')}%`)),
            ),
            like(
              entity('segment').property('id'),
              string().value(`%${input.replace(/\s/g, '%')}%`),
            ),
          ),
        )
        .order(ascending('description'))
        .limit(50),
    [],
  )

  const handleChange = useCallback(
    (selectedSegment: { id?: string; description?: string } | null) => {
      if (!selectedSegment?.id || !selectedSegment?.description) return

      onSelectSegment({
        id: selectedSegment.id,
        description: selectedSegment.description,
      })
      setIsDisabled(true)
    },
    [onSelectSegment],
  )

  return (
    <SelectFromQuery
      queryFactory={segmentListQueryFactory}
      idProperty='id'
      labelProperty='description'
      placeholder={placeholder}
      value={value ?? undefined}
      onDirectChange={handleChange}
      isDisabled={isDisabled}
      isClearable={false}
      debounceMs={250}
      minimumSearchLength={0}
      styles={{
        control: (baseStyles: CSSObjectWithLabel) => ({
          ...baseStyles,
          width: '150px',
          minHeight: '32px',
          fontSize: '0.9rem',
          cursor: 'pointer',
          '@media (max-width: 768px)': {
            width: '100%',
          },
        }),
        valueContainer: (baseStyles: CSSObjectWithLabel) => ({
          ...baseStyles,
          maxHeight: '30px',
          padding: '0 6px',
        }),
        input: (baseStyles: CSSObjectWithLabel) => ({
          ...baseStyles,
          fontSize: '0.9rem',
        }),
        placeholder: (baseStyles: CSSObjectWithLabel) => ({
          ...baseStyles,
          fontSize: '0.9rem',
        }),
        singleValue: (baseStyles: CSSObjectWithLabel) => ({
          ...baseStyles,
          fontSize: '0.9rem',
        }),
        option: (baseStyles: CSSObjectWithLabel) => ({
          ...baseStyles,
          fontSize: '0.9rem',
        }),
        menuList: (baseStyles: CSSObjectWithLabel) => ({
          ...baseStyles,
          maxHeight: '150px',
          overflowX: 'hidden',
        }),
      }}
    />
  )
}

export default memo(SelectSegmentFilter)
